/**
 * LlmPromptAugmentationCard Component.
 *
 * Read-only display of MCP template LLM prompt augmentation information.
 * Shows context, safety rules, and tool hints from template.yaml.
 * Used in SystemTemplateEditPage and other template detail views.
 *
 * Sprint: 173, Task: C3
 */

import { Card, Label } from '@/components/ui';
import { ShieldAlert, Lightbulb, MessageSquareText } from 'lucide-react';

interface LlmPromptAugmentationCardProps {
  llmPrompts?: {
    context?: string;
    safety?: string;
    tool_hints?: Record<string, string>;
  } | null;
}

export const LlmPromptAugmentationCard: React.FC<LlmPromptAugmentationCardProps> = ({
  llmPrompts,
}) => {
  if (!llmPrompts) return null;

  const hasContent = llmPrompts.context || llmPrompts.safety || 
    (llmPrompts.tool_hints && typeof llmPrompts.tool_hints === 'object' && 
     Object.keys(llmPrompts.tool_hints).length > 0);

  if (!hasContent) return null;

  return (
    <Card className="p-6 space-y-4">
      <h2 className="font-semibold text-sm border-b pb-2 flex items-center gap-2">
        <MessageSquareText className="w-4 h-4 text-blue-500" />
        LLM Prompt Augmentation
      </h2>
      <p className="text-xs text-muted-foreground">
        These prompts are injected into the LLM context at runtime. Defined in the template YAML — edit via Templates-as-Code.
      </p>

      {llmPrompts.context && (
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5">
            <MessageSquareText className="w-3.5 h-3.5" />
            Context
          </Label>
          <div className="rounded-md border bg-muted/50 p-3 text-sm whitespace-pre-wrap font-mono text-xs">
            {String(llmPrompts.context)}
          </div>
        </div>
      )}

      {llmPrompts.safety && (
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5 text-orange-500" />
            Safety Rules
          </Label>
          <div className="rounded-md border bg-orange-50 dark:bg-orange-950/20 p-3 text-sm whitespace-pre-wrap font-mono text-xs">
            {String(llmPrompts.safety)}
          </div>
        </div>
      )}

      {llmPrompts.tool_hints && typeof llmPrompts.tool_hints === 'object' && (
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-yellow-500" />
            Tool Hints ({Object.keys(llmPrompts.tool_hints as Record<string, string>).length})
          </Label>
          <div className="rounded-md border bg-yellow-50 dark:bg-yellow-950/20 divide-y">
            {Object.entries(llmPrompts.tool_hints as Record<string, string>).map(([tool, hint]) => (
              <div key={tool} className="px-3 py-2">
                <span className="font-mono text-xs font-medium">{tool}</span>
                <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground italic border-t pt-2">
        ℹ️ Managed in template catalog (template.yaml)
      </div>
    </Card>
  );
};
